'use client';
import cn from 'classnames';
import { ChangeEvent, FocusEvent, useRef, useState } from 'react';
import { FormikHelpers, FormikErrors } from 'formik';
import { useSearchParams } from 'next/navigation';

import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import EmailField from '@/components/form/lib/EmailField/EmailField';
import PasswordField from '@/components/form/lib/PasswordField/PasswordField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import CheckboxField from '@/components/form/lib/InputField/CheckboxField';
import Text from '@/components/common/Text/Text';

import {
  emailValidationError,
  isBrandAmbassadorCode,
  phoneValidationError
} from '@/util/form_validation_errors';
import { phoneMaskPipe } from '@/util/phone';

import { SignupState, SignupState as RegistrationValues } from '@/types/Signup';

import './SignupStep1Form.style.scss';
import { useOnMount } from '@/components/hook/onMount';
import { useSignup } from '@/components/hook/signup';
import { useModal } from '@/components/hook/modal';
import { useAmbassadorRedirectFlag } from '@/components/hook/flags';
import BrandAmbassadorRedirectModal from '@/components/modal/BrandAmbassadorRedirectModal/BrandAmbassadorRedirectModal';
import { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';

export type SignupStep1FormValues = {
  email: string;
  phone: string;
  password: string;
  optinPromotion: boolean;
  optinText: boolean;
  referralCode?: string;
};

interface SignupStep1FormProps {
  user: RegistrationValues;
  referralCopy?: ReferralCopyProps;
}

interface SignupStep1FormProps
  extends Omit<BaseFormProps<SignupStep1FormValues>, 'children' | 'onSubmit'> {
  onSubmit?: (
    values: SignupStep1FormValues,
    actions: FormikHelpers<SignupStep1FormValues>
  ) => void;
}

async function defaultValidation(
  values: SignupStep1FormValues
): Promise<FormikErrors<SignupStep1FormValues>> {
  const { email, phone, password } = values;
  const errors: FormikErrors<SignupStep1FormValues> = {};
  const emailError = await emailValidationError(email);
  const phoneError = phoneValidationError(phone);

  if (emailError) {
    errors.email = emailError;
  }

  if (phoneError) {
    errors.phone = phoneError;
  }

  if (!password) {
    errors.password = 'A password is required';
  }

  if (password.length < 6) {
    errors.password = 'The password field must be at least 6 characters';
  }

  return errors;
}

/** One-shot DOM read of live step1 identity fields for BA redirect handoff. */
function readLiveStep1FromForm(
  formEl: HTMLFormElement | null
): Pick<SignupStep1FormValues, 'email' | 'phone'> {
  if (!formEl) {
    return { email: '', phone: '' };
  }

  const emailInput = formEl.querySelector<HTMLInputElement>(
    'input[name="email"]'
  );
  const phoneInput = formEl.querySelector<HTMLInputElement>(
    'input[name="phone"]'
  );

  return {
    email: emailInput?.value?.trim() || '',
    phone: phoneInput?.value?.trim() || ''
  };
}

export default function SignupStep1Form(props: SignupStep1FormProps) {
  const {
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    referralCopy,
    user,
    validateOnBlur = false,
    validateOnChange = false
  } = props;
  const searchParams = useSearchParams();
  const { value: signupState, actions: signupActions } = useSignup();
  const formRegionRef = useRef<HTMLDivElement>(null);

  // Initial values should never propagate password from state;
  // reset and require user to retype
  initialValues.password = '';

  const [referralValue, setReferralValue] = useState(
    user.referralCode || (searchParams.get('referral') as string)
  );

  const modalTools = useModal();
  const ambassadorRedirectModalEnabled = useAmbassadorRedirectFlag();
  const ambassadorCodeEntered =
    ambassadorRedirectModalEnabled && isBrandAmbassadorCode(referralValue);

  /**
   * Snapshot regular-apply state for BA handoff at CTA time:
   * SignupContext (later steps / membership) + live step1 email/phone.
   * Password and marketing opt-ins (SMS / email) omitted — consent fresh on BA.
   */
  const buildRegularApplySnapshot = (
    liveStep1?: Pick<SignupStep1FormValues, 'email' | 'phone'>
  ): Partial<SignupState> => {
    const formEl = formRegionRef.current?.querySelector('form') ?? null;
    const live = liveStep1 ?? readLiveStep1FromForm(formEl);
    const {
      optinPromotion: _optinPromotion,
      optinText: _optinText,
      password: _password,
      ...safeContext
    } = signupState;

    return {
      ...safeContext,
      email: live.email || signupState.email,
      phone: live.phone || signupState.phone,
      referralCode: referralValue || signupState.referralCode,
      password: ''
    };
  };

  const showAmbassadorRedirectModal = (
    liveStep1?: Pick<SignupStep1FormValues, 'email' | 'phone'>
  ) => {
    const snapshotOverride = liveStep1;
    modalTools.showModal(
      <BrandAmbassadorRedirectModal
        onClose={modalTools.closeModal}
        getRegularApplySnapshot={() =>
          buildRegularApplySnapshot(snapshotOverride)
        }
      />,
      {
        onClose: modalTools.closeModal,
        topClassName: 'BrandAmbassadorRedirectModal-modal'
      }
    );
  };

  useOnMount(() => {
    if (referralValue) {
      handleChangeReferralValue(referralValue);
    }
  });

  const handleChangeReferralValue = (value: string) => {
    const referralCode = value.toUpperCase();
    setReferralValue(referralCode);

    signupActions.updateFormValues({
      referralCode
    });

    if (ambassadorRedirectModalEnabled && isBrandAmbassadorCode(referralCode)) {
      showAmbassadorRedirectModal();
    }
  };

  const handleSubmit = async (values: SignupStep1FormValues) => {
    // Ambassador codes must go through the Brand Ambassador application flow
    if (ambassadorCodeEntered) {
      showAmbassadorRedirectModal({
        email: values.email,
        phone: values.phone
      });
      return;
    }

    // Little hack.
    // referralValue isn't bound to a formik field,
    // so here we are manually passing it to the SignupStep1FormValues
    values.referralCode = referralValue;
    onSubmitSuccess?.(values);
  };

  return (
    <div
      className='SignupStep1Form'
      ref={formRegionRef}
    >
      <h1 className='Signup-step-title'>Start your application!</h1>
      <div className='Signup-progress-bar'>
        <div
          className='Progress-bar-fill'
          style={{ width: '20%' }}
        ></div>
      </div>
      <h3 className='Signup-step-subtitle'>My account</h3>

      <Form<SignupStep1FormValues>
        initialValues={initialValues}
        validate={validate}
        // If an 'onSubmit function' is passed in, that function will override
        // this component's 'handleSubmit' function.
        onSubmit={onSubmit || handleSubmit}
        validateOnBlur={validateOnBlur}
        validateOnChange={validateOnChange}
      >
        <div className='Signup-fields-wrapper'>
          <EmailField
            className='Signup-email'
            name='email'
            label={'Email'}
            data-testid='signup-email'
          />
          <MaskedField
            className='Signup-phone'
            mask={[
              '(',
              /[1-9]/,
              /\d/,
              /\d/,
              ')',
              ' ',
              /\d/,
              /\d/,
              /\d/,
              '-',
              /\d/,
              /\d/,
              /\d/,
              /\d/
            ]}
            pipe={phoneMaskPipe}
            label={'Phone Number'}
            name='phone'
            guide={false}
            type='tel'
            autoComplete='tel'
            placeholder={'(xxx) xxx-xxxx'}
            data-testid='signup-phone'
          />
          <PasswordField
            name='password'
            label='Create a Password'
            data-testid='signup-password'
          />

          <div className='InputField'>
            <label className='InputField-label'>Referral Code (optional)</label>
            <input
              type='text'
              className={cn('InputField-input', {
                success: referralCopy?.successfullyApplied
              })}
              placeholder='None'
              value={referralValue || ''}
              onBlur={(event: FocusEvent<HTMLInputElement>): void => {
                const { value } = event.currentTarget;
                const referralCode = value.toUpperCase();

                handleChangeReferralValue(referralCode);
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                const { value } = event.currentTarget;
                const referralCode = value.toUpperCase();

                setReferralValue(referralCode);
              }}
            />

            <Text
              className={cn('InputField-message', {
                'InputField-success': referralCopy?.successfullyApplied
              })}
              variant='text-s'
            >
              {!ambassadorCodeEntered && referralCopy?.copy}
            </Text>
          </div>
        </div>
        <div className='Optional-checkboxes'>
          <div className='InputField-checkbox'>
            <CheckboxField
              name='optinPromotion'
              className='Signup-promotional-optin'
              label={'(Optional) Sign me up for Vivrelle promotional emails.'}
            />
          </div>
          <div className='InputField-checkbox'>
            <CheckboxField
              name='optinText'
              className='Signup-text-optin'
              label={
                <span>
                  <Text variant='span-s'>
                    (Optional){' '}
                    <Text variant='span-s-500'>Get notified when approved</Text>{' '}
                    plus exclusive discounts and cart reminders from Vivrelle
                    via text. Msg & Data rates may apply. Msg frequency may
                    vary. Reply HELP for help. Reply STOP to opt-out.
                  </Text>{' '}
                  <a
                    href='/terms'
                    className='Signup-terms'
                    target='_blank'
                  >
                    Terms & Conditions.
                  </a>
                </span>
              }
            />
          </div>
        </div>
        <button
          className='Signup-Continue-Button cursor'
          type='submit'
          data-testid='signup-step1-continue'
        >
          Continue
        </button>
      </Form>
    </div>
  );
}
